import { createContext, useContext, useCallback, useEffect, type ReactNode } from 'react';
import { SITE_LOCALE, LOCALE_ORIGINS, type Locale } from '@/lib/site-config';
import en from './en.json';
import nl from './nl.json';
import fr from './fr.json';
import de from './de.json';

export { LOCALES, type Locale } from '@/lib/site-config';

const translations: Record<Locale, Record<string, string>> = { en, nl, fr, de };

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const locale = SITE_LOCALE;

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback(
    (next: Locale) => {
      if (next === locale) return;
      // Cross-domain navigation: each locale lives on its own ccTLD.
      const path = typeof window !== 'undefined' ? window.location.pathname : '/';
      window.location.href = `${LOCALE_ORIGINS[next]}${path === '/' ? '' : path}`;
    },
    [locale],
  );

  const t = useCallback(
    (key: string) => translations[locale][key] ?? translations.en[key] ?? key,
    [locale],
  );

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}

export function useT() {
  return useLocale().t;
}
